import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { mockEvents } from '../data/mockData';
import { EmprendedorUser } from '../types';

const Emprendedor_Dashboard: React.FC = () => {
  const { user } = useAuth();
  const emprendedorUser = user as EmprendedorUser;
  
  const [selectedEvent, setSelectedEvent] = useState('');
  const [salesAmount, setSalesAmount] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Filtrar eventos donde el emprendedor está inscrito
  const myEvents = mockEvents.filter(event => 
    event.participants.includes(emprendedorUser.profile.id)
  );

  const handleReportSales = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedEvent && salesAmount) {
      setShowSuccessMessage(true);
      setSelectedEvent('');
      setSalesAmount('');
      setTimeout(() => setShowSuccessMessage(false), 3000);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl shadow-xl p-8 mb-8 text-white">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <img
                src={emprendedorUser.profile.logoUrl}
                alt={emprendedorUser.profile.brandName}
                className="w-20 h-20 rounded-full border-4 border-white shadow-lg"
              />
              <div>
                <h1 className="text-3xl font-bold mb-1">
                  ¡Hola, {emprendedorUser.name}!
                </h1>
                <p className="text-primary-100 text-lg">
                  {emprendedorUser.profile.brandName}
                </p>
              </div>
            </div>
            <Link
              to="/eventos"
              className="bg-white text-primary-600 px-6 py-3 rounded-xl font-semibold hover:bg-primary-50 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Ver Eventos Disponibles
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Mis Próximos Eventos */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Mis Próximos Eventos</h2>
              
              {myEvents.length > 0 ? (
                <div className="space-y-4">
                  {myEvents.map(event => (
                    <Link
                      key={event.id}
                      to={`/evento/${event.id}`}
                      className="block group"
                    >
                      <div className="border border-gray-200 rounded-xl p-5 hover:border-primary-300 hover:shadow-md transition-all">
                        <div className="flex flex-col sm:flex-row gap-4">
                          <img
                            src={event.bannerImg}
                            alt={event.name}
                            className="w-full sm:w-32 h-24 object-cover rounded-lg"
                          />
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-800 group-hover:text-primary-600 transition-colors mb-2">
                              {event.name}
                            </h3>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                              <div className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {formatDate(event.startDate)} - {formatDate(event.endDate)}
                              </div>
                              <div className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                </svg>
                                {event.location}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-600 mb-4">No tienes eventos próximos</p>
                  <Link
                    to="/eventos"
                    className="inline-block bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Explorar Eventos
                  </Link>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-green-500">
                <p className="text-sm text-gray-600 mb-1">Eventos Activos</p>
                <p className="text-3xl font-bold text-gray-800">{myEvents.length}</p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500">
                <p className="text-sm text-gray-600 mb-1">Total Invertido</p>
                <p className="text-3xl font-bold text-gray-800">{formatPrice(850000)}</p>
              </div>
              <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-purple-500">
                <p className="text-sm text-gray-600 mb-1">Ventas Reportadas</p>
                <p className="text-3xl font-bold text-gray-800">{formatPrice(2500000)}</p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Reportar Ventas */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Reportar Ventas Diarias</h3>
              
              {showSuccessMessage && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 text-sm font-medium">
                    ✅ Ventas reportadas exitosamente
                  </p>
                </div>
              )}

              <form onSubmit={handleReportSales} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seleccionar Evento
                  </label>
                  <select
                    value={selectedEvent}
                    onChange={(e) => setSelectedEvent(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">-- Selecciona un evento --</option>
                    {myEvents.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monto de Ventas (COP)
                  </label>
                  <input
                    type="number"
                    value={salesAmount}
                    onChange={(e) => setSalesAmount(e.target.value)}
                    placeholder="Ej: 500000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                    min="0"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors font-semibold shadow-md hover:shadow-lg"
                >
                  Registrar Ventas
                </button>
              </form>
            </div>

            {/* Perfil */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Mi Perfil</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Marca</p>
                  <p className="font-semibold text-gray-800">{emprendedorUser.profile.brandName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Descripción</p>
                  <p className="text-gray-700 text-sm">{emprendedorUser.profile.description}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Instagram</p>
                  <p className="text-primary-600 font-medium">{emprendedorUser.profile.social.instagram}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Emprendedor_Dashboard;
